import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import { TOOL_ID } from '@/consts/configs';
import getDriveFiles from '@/requests/cloud-storage/getDriveFiles';
import getRemoteSettings from '@/utils/user/getRemoteSettings';
import isAuthorized from '@/utils/user/isAuthorized';
import withTimeout from '@/utils/async/withTimeout';
import { isPortfolioApp } from '@/utils/environment/surface';

import DEFAULT_ONBOARDING_DECK from './consts/deck';
import {
  readOnboardingRecord,
  saveOnboardingRecord,
} from './storage/onboardingRecord';
import type { DeckEntry } from './consts/deck';
import type { OnboardingState } from './types';

const READ_TIMEOUT_MS = 3000;

const DECK_SETTING_NAME = 'genai_studio_onboarding_deck';

const HIDDEN: OnboardingState = {
  shouldShow: false,
  categories: [],
  fileId: null,
  deck: [],
};

/**
 * An empty list is invalid, not "no onboarding": the host answers an
 * unregistered name with `{}`, and a deck that silently empties would close the
 * flow and burn the user's one chance at it.
 */
const isDeckSetting = (
  settings: unknown
): settings is { cards: DeckEntry[] } => {
  const { cards } = (settings ?? {}) as { cards: DeckEntry[] };

  return (
    Array.isArray(cards) &&
    cards.length > 0 &&
    cards.every(
      (card) =>
        typeof card?.category === 'string' && typeof card?.preset === 'string'
    )
  );
};

/**
 * Fails closed: any unreadable answer hides onboarding. Showing it to an
 * established user costs more than missing a new one.
 */
const resolveOnboardingState = async (): Promise<OnboardingState> => {
  if (!isPortfolioApp() || !isAuthorized()) return HIDDEN;

  const record = await withTimeout({
    promise: readOnboardingRecord(),
    timeoutMs: READ_TIMEOUT_MS,
    fallback: null,
  });

  if (!record) return HIDDEN;

  if (record.isCompleted) {
    return {
      shouldShow: false,
      categories: record.categories,
      fileId: record.fileId,
      deck: [],
    };
  }

  const creations = await withTimeout({
    promise: getDriveFiles({ toolId: TOOL_ID, limit: 1 }),
    timeoutMs: READ_TIMEOUT_MS,
    fallback: null,
  });

  if (!creations) return HIDDEN;

  // Onboarding shipped after the app did, so an established user has no record
  // either. Write an empty one so this probe never runs for them again.
  if (creations.files.length > 0) {
    saveOnboardingRecord({ categories: [], fileId: null }).catch((error) =>
      errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN)
    );

    return HIDDEN;
  }

  const setting = await withTimeout({
    promise: getRemoteSettings({
      name: DECK_SETTING_NAME,
      tag: DECK_SETTING_NAME,
      defaultValue: { cards: DEFAULT_ONBOARDING_DECK },
      validate: isDeckSetting,
    }),
    timeoutMs: READ_TIMEOUT_MS,
    fallback: { cards: DEFAULT_ONBOARDING_DECK },
  });

  return {
    shouldShow: true,
    categories: [],
    fileId: null,
    deck: setting.cards,
  };
};

export default resolveOnboardingState;
